import { useEffect, useMemo, useRef, useState } from 'react'
import type { Brand } from './api'
import { BRAND_PATHS } from './icons'

/** Each provider's colour first, then alternates for a second, third… account of the same kind (white marks stay legible on all). */
const TILE: Record<Brand, string[]> = {
  gmail: ['#EA4335', '#34A853', '#4285F4', '#E37400'],
  icloud: ['#3693F3', '#5E5CE6', '#14A3B8', '#AF52DE'],
  outlook: ['#0078D4', '#038387', '#8764B8', '#CA5010'],
  yahoo: ['#6001D2', '#C2187A', '#2E5BD9', '#0F8A6E'],
  fastmail: ['#2C5FA5', '#1B8A5A', '#B4469A', '#C2410C'],
  imap: ['#5E5E5E', '#3A6EA5', '#7B5EA7', '#2F8A6B'],
  test: ['#5E5E5E', '#3A6EA5', '#7B5EA7', '#2F8A6B'],
}
export const tileColor = (brand: Brand, tone = 0) => { const list = TILE[brand] ?? TILE.imap; return list[tone % list.length]! }
/** Which colour an item gets among others of the same provider, in list order. */
export function tones<T extends { id: string; brand: Brand }>(items: T[]): Map<string, number> {
  const seen = new Map<Brand, number>(), out = new Map<string, number>()
  for (const item of items) { const n = seen.get(item.brand) ?? 0; out.set(item.id, n); seen.set(item.brand, n + 1) }
  return out
}

/** A provider's app tile: its mark in white on its brand colour (or an alternate, to tell same-provider accounts apart). */
export function Tile({ brand, size = 16, tone = 0 }: { brand: Brand; size?: number; tone?: number }) {
  const path = BRAND_PATHS[brand]
  const radius = Math.max(3, Math.round(size * 0.26))
  return (
    <span className="tile" style={{ width: size, height: size, borderRadius: radius, background: tileColor(brand, tone) }} aria-hidden="true">
      {path
        ? <svg width={Math.round(size * 0.62)} height={Math.round(size * 0.62)} viewBox="0 0 24 24"><path fill="#fff" d={path} /></svg>
        : <span style={{ fontSize: Math.round(size * 0.58), lineHeight: 1, fontWeight: 700, color: '#fff' }}>{brand === 'fastmail' ? 'F' : '@'}</span>}
    </span>
  )
}

// Free and personal mail domains: a favicon would be the provider's, not the person's.
const PERSONAL_DOMAINS = new Set(['gmail.com', 'googlemail.com', 'icloud.com', 'me.com', 'mac.com', 'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
  'yahoo.com', 'ymail.com', 'aol.com', 'proton.me', 'protonmail.com', 'pm.me', 'fastmail.com', 'hey.com', 'gmx.com', 'zoho.com', 'mm.test'])

const hash = (value: string) => { let h = 2166136261; for (const c of value) h = Math.imul(h ^ c.charCodeAt(0), 16777619); return h >>> 0 }
const BAYER = [[0, 8, 2, 10], [12, 4, 14, 6], [3, 11, 1, 9], [15, 7, 13, 5]]
const PAIRS: Array<[string, string]> = [
  ['#FF9F43', '#781C24'], ['#5EB6FF', '#16266E'], ['#8CE6BE', '#10545C'], ['#C496FF', '#3C1E78'],
  ['#FFD166', '#7A3A10'], ['#FF7EB6', '#5C1238'], ['#7EE0FF', '#0E3E5C'], ['#B8E986', '#1E4A1A'],
]

const initials = (name: string, email: string) => {
  const words = (name || email.split('@')[0] || '?').replace(/[^\p{L}\p{N}\s]/gu, ' ').trim().split(/\s+/).filter(Boolean)
  return ((words[0]?.[0] ?? '?') + (words.length > 1 ? words[words.length - 1]![0] : '')).toUpperCase()
}

/** A dithered two-tone gradient behind a person's initials, the same colours for the same person every time. */
function ditherDataUrl(seed: string): string {
  const [light, dark] = PAIRS[hash(seed) % PAIRS.length]!
  const cells = 22, cell = 4, size = cells * cell
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = size
  const ctx = canvas.getContext('2d')!
  for (let y = 0; y < cells; y++) for (let x = 0; x < cells; x++) {
    const t = (x + y) / (2 * (cells - 1))
    ctx.fillStyle = t > (BAYER[y % 4]![x % 4]! + 0.5) / 16 ? dark : light
    ctx.fillRect(x * cell, y * cell, cell, cell)
  }
  return canvas.toDataURL()
}
const ditherCache = new Map<string, string>()
/** Domains with no icon; they show initials from then on. */
const failed = new Set<string>()

/**
 * Favicon tone: is it a logo on its own solid background ('solid'), or a mark on a
 * transparent background, mostly dark ('dark') or mostly light ('light')? Transparent
 * marks get a plate in the opposite tone so they show on any theme. Measured once per
 * domain from the same-origin favicon and remembered.
 */
type Tone = 'solid' | 'dark' | 'light' | 'mid'
const TONE_KEY = 'mm.faviconTones'
const faviconTones: Record<string, Tone> = (() => { try { return JSON.parse(localStorage.getItem(TONE_KEY) || '{}') } catch { return {} } })()
let toneSave = 0
function measureTone(image: HTMLImageElement): Tone {
  const size = 32
  const canvas = document.createElement('canvas'); canvas.width = canvas.height = size
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return 'solid'
  ctx.drawImage(image, 0, 0, size, size)
  const { data } = ctx.getImageData(0, 0, size, size)
  let clear = 0, opaque = 0, lum = 0
  // The four corners and edges decide whether there's a background of its own.
  let edgeClear = 0, edge = 0
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const i = (y * size + x) * 4, a = data[i + 3]! / 255
    const onEdge = x < 2 || y < 2 || x >= size - 2 || y >= size - 2
    if (onEdge) { edge++; if (a < 0.2) edgeClear++ }
    if (a < 0.2) { clear++; continue }
    opaque++
    lum += (0.2126 * data[i]! + 0.7152 * data[i + 1]! + 0.0722 * data[i + 2]!) / 255
  }
  // A transparent corner means the mark has no background of its own (a rounded app tile still fills its edges).
  const cornerClear = [[0, 0], [size - 1, 0], [0, size - 1], [size - 1, size - 1]].filter(([x, y]) => data[(y! * size + x!) * 4 + 3]! < 50).length
  if (!opaque || clear / (size * size) < 0.15 || (cornerClear < 2 && edgeClear / edge < 0.5)) return 'solid'
  // Rounded tiles (Resend, OpenAI…) have clear corners but solid edges: they already have a background.
  if (edgeClear / edge < 0.25 && clear / (size * size) < 0.3 && cornerClear === 4 && (lum / opaque) > 0.85) return 'solid'
  const avg = lum / opaque
  return avg < 0.35 ? 'dark' : avg > 0.72 ? 'light' : 'mid'
}

/** Sender picture: the company's website icon, or dithered initials for a person. The account tile sits on its corner. */
export function Avatar({ name, email, person, brand, tone = 0, size = 22, ring }: { name: string; email: string; person: boolean; brand?: Brand; tone?: number; size?: number; ring?: string }) {
  const domain = email.split('@')[1]?.toLowerCase() ?? ''
  const [broken, setBroken] = useState(() => failed.has(domain))
  const [faviconTone, setFaviconTone] = useState<Tone | undefined>(() => faviconTones[domain])
  const imgRef = useRef<HTMLImageElement>(null)
  const learnTone = (image: HTMLImageElement) => {
    if (faviconTones[domain] || !image.naturalWidth) return
    try { const t = measureTone(image); faviconTones[domain] = t; setFaviconTone(t); clearTimeout(toneSave); toneSave = window.setTimeout(() => { try { localStorage.setItem(TONE_KEY, JSON.stringify(faviconTones)) } catch {} }, 1000) } catch { /* unreadable: leave as is */ }
  }
  // Cached images can finish before React wires onLoad; measure those too.
  useEffect(() => { const image = imgRef.current; if (image?.complete) learnTone(image); else if (faviconTones[domain] !== faviconTone) setFaviconTone(faviconTones[domain]) }, [domain])
  const useFavicon = !person && domain && !PERSONAL_DOMAINS.has(domain) && !broken
  const seed = (email || name).toLowerCase()
  const background = useMemo(() => {
    if (useFavicon) return null
    let url = ditherCache.get(seed)
    if (!url) ditherCache.set(seed, url = ditherDataUrl(seed))
    return url
  }, [seed, useFavicon])
  const badge = Math.round(size * 0.5)
  return (
    <span className="avatar" style={{ width: size + 3, height: size + 3 }}>
      {useFavicon
        ? <span className={`avatar-face favicon${faviconTone && faviconTone !== 'solid' ? ` tone-${faviconTone}` : ''}`} style={{ width: size, height: size, borderRadius: Math.round(size * 0.24) }}>
            <img src={`/api/favicon/${encodeURIComponent(domain)}`} alt="" width={size} height={size} loading="lazy"
              onError={() => { failed.add(domain); setBroken(true) }}
              ref={imgRef} onLoad={event => learnTone(event.currentTarget)} />
          </span>
        : <span className="avatar-face initials" style={{ width: size, height: size, backgroundImage: `url(${background})`, fontSize: Math.max(8, Math.round(size * 0.38)) }}>
            {initials(name, email)}
          </span>}
      {brand && <span className="avatar-badge" style={{ background: ring }}><Tile brand={brand} size={badge} tone={tone} /></span>}
    </span>
  )
}

/** "3h", "2d", "Sep 12": compact, for row edges. */
export function age(iso: string, now = Date.now()): string {
  const ms = now - Date.parse(iso)
  if (ms < 60_000) return 'now'
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m`
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h`
  if (ms < 7 * 86_400_000) return `${Math.floor(ms / 86_400_000)}d`
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/** "Tue 9:41 AM" for message headers. */
export function stamp(iso: string): string {
  const date = new Date(iso)
  const today = new Date()
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  if (date.toDateString() === today.toDateString()) return time
  if (today.getTime() - date.getTime() < 6 * 86_400_000) return `${date.toLocaleDateString(undefined, { weekday: 'short' })} ${time}`
  return `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric' })} ${time}`
}

const FREE_MAIL = /^(gmail|googlemail|icloud|me|mac|outlook|hotmail|live|msn|yahoo|ymail|aol|proton|protonmail|pm|fastmail|hey|gmx|zoho)\./

/** The part of a hostname a person recognises: mcinfo.ups.com → ups.com (good enough without a suffix list for display). */
export function siteOf(email: string): string {
  const host = (email.split('@')[1] ?? '').toLowerCase()
  const parts = host.split('.')
  const twoLevel = parts.length > 2 && /^(co|com|org|net|ac|gov)$/.test(parts[parts.length - 2]!) && parts[parts.length - 1]!.length === 2
  return parts.slice(twoLevel ? -3 : -2).join('.')
}

/**
 * Who really sent it, in plain words. Flags a display name that claims a brand the
 * sending address doesn't belong to ("Cloudflare" from inbound.new), and names a
 * forwarding service ("Cloudflare via Inbound").
 */
export function senderCheck(from: { name: string; email: string }, person: boolean): { mismatch: boolean; claimed: string; site: string; via: string } {
  const site = siteOf(from.email)
  const viaMatch = /^(.*?)\s+via\s+(.+)$/i.exec(from.name.trim())
  const claimed = (viaMatch ? viaMatch[1]! : from.name).replace(/["']/g, '').trim()
  const via = viaMatch ? viaMatch[2]!.trim() : ''
  const token = claimed.toLowerCase().split(/[\s,|·-]+/).find(word => word.length >= 3) ?? ''
  const compact = site.replace(/\.[a-z]+$/, '').replace(/[^a-z0-9]/g, '')
  const mismatch = !!token && !person && !FREE_MAIL.test(site) && !compact.includes(token.replace(/[^a-z0-9]/g, '')) && !token.includes(compact)
  return { mismatch, claimed, site, via }
}
